import { createSlice } from '@reduxjs/toolkit';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  events: [],
  projectTasks: [],
  projectTasksLoading: false,
  talentBookings: [],
  talentBookingsLoading: false,
  hiredTalentSchedules: [],
  hiredTalentSchedulesLoading: false,
};

const slice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    // GET EVENTS
    getEventsSuccess(state, action) {
      state.events = action.payload;
    },

    // GET PROJECT TASKS
    getProjectTasksStart(state) {
      state.projectTasksLoading = true;
    },
    getProjectTasksSuccess(state, action) {
      state.projectTasks = action.payload;
      state.projectTasksLoading = false;
    },
    getProjectTasksFailure(state) {
      state.projectTasksLoading = false;
    },

    // GET TALENT BOOKINGS (hired-talent bookings I created)
    getTalentBookingsStart(state) {
      state.talentBookingsLoading = true;
    },
    getTalentBookingsSuccess(state, action) {
      state.talentBookings = action.payload;
      state.talentBookingsLoading = false;
    },
    getTalentBookingsFailure(state) {
      state.talentBookingsLoading = false;
    },

    // GET HIRED TALENT SCHEDULES (weekly availability for my hired talents)
    getHiredTalentSchedulesStart(state) {
      state.hiredTalentSchedulesLoading = true;
    },
    getHiredTalentSchedulesSuccess(state, action) {
      state.hiredTalentSchedules = action.payload;
      state.hiredTalentSchedulesLoading = false;
    },
    getHiredTalentSchedulesFailure(state) {
      state.hiredTalentSchedulesLoading = false;
    },

    // CREATE EVENT
    createEventSuccess(state, action) {
      state.events = [...state.events, action.payload];
    },

    // UPDATE EVENT
    updateEventSuccess(state, action) {
      const event = action.payload;

      state.events = state.events.map((_event) => {
        if (_event.id === event.id) {
          return event;
        }
        return _event;
      });
    },

    // DELETE EVENT
    deleteEventSuccess(state, action) {
      const eventId = String(action.payload);
      state.events = state.events.filter((event) => String(event.id) !== eventId);
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getEvents() {
  return async (dispatch) => {
    try {
      const response = await axios.get(API_ENDPOINTS.calendar.events);
      // console.log('getEvents response:', response.data);
      const events = response.data.events || response.data.data || [];
      // Transform backend format to frontend format
      const transformedEvents = events.map(event => ({
        id: String(event.id),
        title: event.title,
        description: event.description,
        color: event.color,
        allDay: event.allDay,
        start: event.startDate,
        end: event.endDate,
        clientId: event.clientId || '',
      }));
      // console.log('transformedEvents:', transformedEvents);
      dispatch(slice.actions.getEventsSuccess(transformedEvents));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function getProjectTasks() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getProjectTasksStart());
      const response = await axios.get(API_ENDPOINTS.tasks.myCalendarTasks);
      if (response.data.success) {
        dispatch(slice.actions.getProjectTasksSuccess(response.data.data));
      } else {
        dispatch(slice.actions.getProjectTasksFailure());
      }
    } catch (error) {
      console.error('Error fetching project tasks for calendar:', error);
      dispatch(slice.actions.getProjectTasksFailure());
    }
  };
}

// ----------------------------------------------------------------------

export function getHiredTalentSchedules() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getHiredTalentSchedulesStart());
      const response = await axios.get(API_ENDPOINTS.talent.hiredSchedules);
      if (response.data?.success) {
        dispatch(slice.actions.getHiredTalentSchedulesSuccess(response.data.data || []));
      } else {
        dispatch(slice.actions.getHiredTalentSchedulesFailure());
      }
    } catch (error) {
      console.error('Error fetching hired talent schedules:', error);
      dispatch(slice.actions.getHiredTalentSchedulesFailure());
    }
  };
}

// ----------------------------------------------------------------------

export function getTalentBookings() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getTalentBookingsStart());
      const response = await axios.get(API_ENDPOINTS.talentBookings.mySent);
      if (response.data?.success) {
        dispatch(slice.actions.getTalentBookingsSuccess(response.data.data || []));
      } else {
        dispatch(slice.actions.getTalentBookingsFailure());
      }
    } catch (error) {
      console.error('Error fetching talent bookings:', error);
      dispatch(slice.actions.getTalentBookingsFailure());
    }
  };
}

// ----------------------------------------------------------------------

export function createEvent(eventData) {
  return async (dispatch) => {
    try {
      const data = {
        title: eventData.title,
        description: eventData.description,
        color: eventData.color,
        allDay: eventData.allDay,
        endDate: eventData.end,
        startDate: eventData.start,
        clientId: eventData.clientId || null,
      };
      const response = await axios.post(API_ENDPOINTS.calendar.events, data);
      const newEvent = response.data.event || response.data.data;
      if (newEvent) {
        // Transform backend response to frontend format
        const transformedEvent = {
          id: String(newEvent.id),
          title: newEvent.title,
          description: newEvent.description,
          color: newEvent.color,
          allDay: newEvent.allDay,
          start: newEvent.startDate,
          end: newEvent.endDate,
          clientId: newEvent.clientId || '',
        };
        // console.log('createEvent transformedEvent:', transformedEvent);
        dispatch(slice.actions.createEventSuccess(transformedEvent));
      }
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function updateEvent(eventId, eventData) {
  return async (dispatch) => {
    try {
      const data = {
        title: eventData.title,
        description: eventData.description,
        color: eventData.color,
        allDay: eventData.allDay,
        endDate: eventData.end,
        startDate: eventData.start,
        clientId: eventData.clientId || null,
      };
      const response = await axios.put(API_ENDPOINTS.calendar.event(eventId), data);
      const updatedEvent = response.data.event || response.data.data;
      if (updatedEvent) {
        // Transform backend response to frontend format
        dispatch(slice.actions.updateEventSuccess({
          id: String(updatedEvent.id),
          title: updatedEvent.title,
          description: updatedEvent.description,
          color: updatedEvent.color,
          allDay: updatedEvent.allDay,
          start: updatedEvent.startDate,
          end: updatedEvent.endDate,
          clientId: updatedEvent.clientId || '',
        }));
      }
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function deleteEvent(eventId) {
  return async (dispatch) => {
    try {
      await axios.delete(API_ENDPOINTS.calendar.event(eventId));
      dispatch(slice.actions.deleteEventSuccess(eventId));
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };
}
